import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Check } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import * as React from "react"
import { PropsWithChildren, useCallback, useEffect, useState } from "react"
import { cn } from "src/common/utils"
import { LoadingSpinner } from "src/components/ui/loading-spinner"

const buttonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/85",
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/85",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/85",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-accent hover:border-foreground/15",
        secondary: "bg-accent text-foreground shadow-sm hover:bg-muted",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline",
      },
      size: {
        default: "h-8 px-4 py-2",
        xs: "h-6 rounded-md px-2 text-xs max-md:text-xs",
        sm: "h-7 rounded-md px-3 text-xs max-md:text-xs max-md:px-2 max-md:h-6",
        lg: "h-9 rounded-md px-8",
        xl: "h-12 rounded-md px-12 text-lg",
        icon: "h-8 w-8",
        link: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  loadingText?: string
  successText?: string
  successDuration?: number
}

const Button = React.forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  ({ className, variant, size, asChild = false, loading, loadingText, successText, successDuration = 2000, disabled, children, onClick, ...props }, ref) => {
    const [showSuccess, setShowSuccess] = useState(false)
    const [wasLoading, setWasLoading] = useState(false)

    const handleSuccessEnd = useCallback(() => {
      setShowSuccess(false)
    }, [])

    useEffect(() => {
      let timer: ReturnType<typeof setTimeout> | undefined
      if (loading) {
        setWasLoading(true)
      } else if (wasLoading && successText) {
        setShowSuccess(true)
        setWasLoading(false)
        timer = setTimeout(handleSuccessEnd, successDuration)
      } else {
        setWasLoading(false)
      }
      return () => {
        if (timer) clearTimeout(timer)
      }
    }, [loading, wasLoading, successText, successDuration, handleSuccessEnd])

    if (asChild) {
      return <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>{children}</Slot>
    }

    const isDisabled = disabled || loading

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }), loading && "cursor-not-allowed")}
        ref={ref}
        disabled={isDisabled}
        onClick={onClick}
        {...props}
      >
        <AnimatePresence mode="wait" initial={false}>
          {loading ? (
            <motion.span
              key="loading"
              className="inline-flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1 }}
            >
              <LoadingSpinner className="h-4 w-4" />
              {loadingText && <span>{loadingText}</span>}
            </motion.span>
          ) : showSuccess ? (
            <motion.span
              key="success"
              className="inline-flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1 }}
            >
              <Check className="h-4 w-4" />
              <span>{successText}</span>
            </motion.span>
          ) : (
            <motion.span
              key="children"
              className="inline-flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1 }}
            >
              {children}
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
